<template>
  <div class="card">
    <div class="table-responsive">
      <table
        class="table table-sm align-middle mb-0"
        data-testid="forward-pools"
      >
        <thead>
          <tr>
            <th class="ps-3">
              {{ $t("soldera_inventory.forward.columns.technology") }}
            </th>
            <th>{{ $t("soldera_inventory.forward.columns.country") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.scheme") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.cod_age") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.support") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.ecolabels") }}</th>
            <th v-for="period in periods" :key="period" class="text-end">
              {{ period }}
            </th>
            <th class="text-end">
              {{ $t("soldera_inventory.forward.columns.capacity") }}
            </th>
            <th class="pe-3 take-column">
              {{ $t("soldera_inventory.forward.columns.take") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="pools.length === 0">
            <td
              :colspan="8 + periods.length"
              class="text-center text-body-tertiary py-4"
              data-testid="no-pools"
            >
              {{ $t("soldera_inventory.forward.no_pools") }}
            </td>
          </tr>
          <tr
            v-for="pool in pools"
            :key="pool.id"
            :data-testid="`pool-${pool.id}`"
          >
            <td class="ps-3">
              {{ pool.technologies.map(technologyName).join(", ") }}
            </td>
            <td>{{ pool.countries.join(", ") }}</td>
            <td>{{ pool.registration_authority }}</td>
            <td>{{ pool.oldest_cod_age ?? "–" }}</td>
            <td>
              {{ $t(`forwards.common.support.${pool.support}`) }}
            </td>
            <td>
              <span
                v-for="key in pool.ecolabels"
                :key="key"
                class="badge rounded-pill me-1"
                :class="ecolabelBadgeClass(key)"
              >
                {{ ecolabelBrandName(key) }}
              </span>
              <span v-if="pool.ecolabels.length === 0">–</span>
            </td>
            <td
              v-for="period in periods"
              :key="period"
              class="text-end tabular-nums"
            >
              {{ pool.capacity_by_period[period] ?? 0 }}
            </td>
            <td class="text-end tabular-nums">{{ pool.capacity_total }}</td>
            <td class="pe-3">
              <input
                type="number"
                class="form-control form-control-sm"
                min="0"
                step="1"
                :max="pool.capacity_total"
                :value="takes[pool.id] ?? ''"
                :aria-label="$t('soldera_inventory.forward.columns.take')"
                :data-testid="`take-${pool.id}`"
                @input="onTakeInput(pool, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TakeMap } from "@/forwards2/picker/distributeTakes";
import { technologyName } from "@/types/technology";
import { ecolabelBadgeClass, ecolabelBrandName } from "@/utils/ecolabelBadges";

import type { ForwardInventoryPool } from "../solderaInventoryTypes";

/**
 * The forward pools a buyer may take from, one row per pool described by what it produces, with a
 * whole-MWh take input capped at what the pool can still sell over the picked periods.
 */
defineProps<{
  pools: ForwardInventoryPool[];
  periods: string[];
}>();

const takes = defineModel<TakeMap>("takes", { required: true });

function onTakeInput(pool: ForwardInventoryPool, event: Event) {
  const raw = Number((event.target as HTMLInputElement).value);
  const volume = Number.isFinite(raw)
    ? Math.min(Math.max(Math.floor(raw), 0), pool.capacity_total)
    : 0;
  const next = { ...takes.value };
  if (volume > 0) next[pool.id] = volume;
  else delete next[pool.id];
  takes.value = next;
}
</script>

<style lang="scss" scoped>
.take-column {
  width: 120px;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
